import { AuthLayout } from '@/bundle/Auth/ui/AuthLayout/AuthLayout';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { AuthStep } from '@/bundle/Auth/ui/AuthStep/AuthStep';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { useFormik } from 'formik';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikPasswordInput } from '@/components/form/fields/FormikPasswordInput/FormikPasswordInput';
import { getForgotPasswordCheckEmailPath } from '@/bundle/Auth/ForgotPasswordFlow/ForgotPasswordCheckEmailPage/path/path';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { Box } from '@/components/Box/Box';
import { Button } from '@/components/Button/Button';
import {
  LOGIN_ERROR_NOTIFICATION_OFFSET,
  PASSWORD_VALIDATION_SCHEMA,
} from '@/bundle/Auth/LoginFlow/LoginPage/const/const';
import { Notification } from '@/components/Notification/Notification';
import { useMutation } from '@tanstack/react-query';
import { loginVerifyPassword } from '@/bundle/Auth/LoginFlow/LoginVerifyPassword/api';
import { useAuthNavigateToStep } from '@/bundle/Auth/hooks/useAuthNavigateToStep';
import { LoginVerifyPasswordType } from '@/api/v1/users/loginVerifyPasswordApi';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { NavigationLink } from '@/components/NavigationLink/NavigationLink';
import { useAuthNavigateToLogin } from '@/bundle/Auth/hooks/useAuthNavigateToLogin';

export const LoginVerifyPassword = () => {
  const { t } = useTranslation(['login', 'form']);
  const navigateToStep = useAuthNavigateToStep();
  const location = useLocation();
  const { authNavigateToLogin } = useAuthNavigateToLogin();

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['login_verify_password'],
    mutationFn: (payload: LoginVerifyPasswordType) => {
      return loginVerifyPassword(payload);
    },
    onSuccess(response) {
      if (response.error) return;

      return navigateToStep(response?.body);
    },
  });

  const formik = useFormik({
    initialValues: {
      password: '',
    },
    validationSchema: PASSWORD_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      mutate(values);
    },
  });

  const error = data?.error;
  const formError = getResponseError(error);

  return (
    <AuthLayout>
      <AuthStep>
        <Typography variant='headline4' fontWeight='semibold' color={COLORS.grey[900]} marginBottom='24px'>
          {t('login:passwordHeader')}
        </Typography>

        {formError && (
          <Notification variant='error' {...LOGIN_ERROR_NOTIFICATION_OFFSET}>
            {formError}
          </Notification>
        )}

        <FormikForm value={formik}>
          <FormikPasswordInput
            label={t('form:label.password')}
            placeholder={t('form:placeholder.password')}
            name='password'
            apiError={error}
            hasToggleEye
            labelHint={
              <NavigationLink
                to={getForgotPasswordCheckEmailPath()}
                onClick={() => amplitudeService.logEvent(AMPLITUDE_EVENTS.ForgotPasswordStartRedirect)}
                state={location.state}
              >
                {t('login:forgotPasswordLink')}
              </NavigationLink>
            }
          />
          <Box display='flex' flexDirection='column' marginTop='12px' rowGap='12px'>
            <Button type='submit' isLoading={isPending}>
              {t('login:loginButtonLabel')}
            </Button>
            <Button variant='secondary' onClick={authNavigateToLogin}>
              {t('login:backButtonLabel')}
            </Button>
          </Box>
        </FormikForm>
      </AuthStep>
    </AuthLayout>
  );
};
